import React, { useRef, useState } from 'react';
const BASEURL = process.env.REACT_APP_API_URL;

const VariantConfigurationSection = ({
  enableVariants,
  setIsVariantModalOpen,
  showGeneratedVariants,
  generatedVariants,
  handleDeleteVariantImage,
  handleVariantImageUpload,
  handleDeleteVariantClick,
  imageUploadLoading,
  setGeneratedVariants,
}) => {
  // Add fileInputRefs to handle file inputs without scrolling
  const fileInputRefs = useRef({});
  // State for barcode scanning
  const [barcodeScanning, setBarcodeScanning] = useState({});
  // State for manual barcode entry modal
  const [manualBarcodeModal, setManualBarcodeModal] = useState({
    isOpen: false,
    variantIndex: null,
    inputValue: '',
    error: ''
  });
  // State for barcode update loading
  const [barcodeUpdateLoading, setBarcodeUpdateLoading] = useState({});
  // Add state to track original barcode values
  const [originalBarcodes, setOriginalBarcodes] = useState({});

  // Function to trigger file input without causing scroll jumps
  const triggerFileInput = (index) => {
    if (fileInputRefs.current[index]) {
      // Save current scroll position
      const scrollPosition = window.pageYOffset;
      
      // Click the input
      fileInputRefs.current[index].click();
      
      // Restore scroll position after a short delay
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 0);
    }
  };

  console.log('Generated Variants:', generatedVariants);

  // Function to handle image upload maintaining scroll position
  const handleImageUpload = (e, index) => {
    // Save scroll position
    const scrollPosition = window.pageYOffset;
    
    // Process the file upload
    handleVariantImageUpload(e, index);
    
    // Reset the file input value to allow selecting the same file again
    e.target.value = '';
    
    // Restore scroll position
    setTimeout(() => {
      window.scrollTo(0, scrollPosition);
    }, 0);
  };

  // ADDED: Function to handle barcode API update
  const handleBarcodeUpdate = async (variant, index, newBarcode) => {
    // Only make API call if variant has a valid ID and it's not a temporary variant
    if (!variant.id || variant.id.toString().startsWith('var-')) {
      return;
    }

    setBarcodeUpdateLoading(prev => ({ ...prev, [index]: true }));

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${BASEURL}/product/UpdateCodeABarre`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          idProductVariant: variant.id,
          codeABarre: newBarcode || ''
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Barcode updated successfully:', data);
      
      // Show success toast for API update
      showSuccessToast(`Code-barres mis à jour avec succès`);
      
    } catch (error) {
      console.error('Error updating barcode:', error);
      showErrorToast('Erreur lors de la mise à jour du code-barres');
      
      // Revert the barcode change on error
      const newVariants = [...generatedVariants];
      newVariants[index].barcode = variant.barcode; // Revert to original value
      setGeneratedVariants(newVariants);
    } finally {
      setBarcodeUpdateLoading(prev => ({ ...prev, [index]: false }));
    }
  };

  // ENHANCED: Function to handle barcode input change with API call
  const handleBarcodeInputChange = (e, index) => {
    const newBarcode = e.target.value;
    
    // Update local state immediately - Fix property name
    const newVariants = [...generatedVariants];
    newVariants[index].barCode = newBarcode; // Changed from .barcode to .barCode
    setGeneratedVariants(newVariants);
  };

  // ENHANCED: Function to handle barcode input focus (store original value)
  const handleBarcodeFocus = (index, currentBarcode) => {
    setOriginalBarcodes(prev => ({ ...prev, [index]: currentBarcode }));
  };

  // ENHANCED: Function to handle barcode input blur (when user finishes typing)
  const handleBarcodeBlur = (variant, index, newBarcode) => {
    const originalBarcode = originalBarcodes[index];
    
    // Only make API call if barcode actually changed
    if (originalBarcode !== newBarcode) {
      handleBarcodeUpdate(variant, index, newBarcode);
    }
    
    // Clean up the stored original value
    setOriginalBarcodes(prev => {
      const newState = { ...prev };
      delete newState[index];
      return newState;
    });
  };

  // Function to open manual barcode entry modal
  const openManualBarcodeModal = (index, reason = 'manual') => {
    setManualBarcodeModal({
      isOpen: true,
      variantIndex: index,
      inputValue: generatedVariants[index]?.barCode || '', // Changed from .barcode to .barCode
      error: '',
      reason
    });
  };

  // Function to close manual barcode entry modal
  const closeManualBarcodeModal = () => {
    setManualBarcodeModal({
      isOpen: false,
      variantIndex: null,
      inputValue: '',
      error: ''
    });
  };

  // ENHANCED: Function to handle manual barcode submission with API call
  const handleManualBarcodeSubmit = async () => {
    const { variantIndex, inputValue } = manualBarcodeModal;
    
    if (!inputValue.trim()) {
      setManualBarcodeModal(prev => ({
        ...prev,
        error: 'Veuillez saisir un code-barres valide'
      }));
      return;
    }

    const variant = generatedVariants[variantIndex];
    const trimmedBarcode = inputValue.trim();

    // Update the variant with the manual barcode
    const newVariants = [...generatedVariants];
    newVariants[variantIndex].barCode = trimmedBarcode; // Changed from .barcode to .barCode
    setGeneratedVariants(newVariants);

    // Close modal
    closeManualBarcodeModal();
    
    // Show success toast
    showSuccessToast(`Code-barres ajouté: ${trimmedBarcode}`);

    // Make API call to update barcode
    await handleBarcodeUpdate(variant, variantIndex, trimmedBarcode);
  };

  // Function to show success toast
  const showSuccessToast = (message) => {
    // Create toast element
    const toastHtml = `
      <div id="success-toast" class="fixed top-4 right-4 z-50 transform transition-all duration-300 translate-x-full">
        <div class="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3 max-w-sm">
          <div class="flex-shrink-0">
            <svg class="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <div class="flex-1">
            <p class="text-sm font-medium">${message}</p>
          </div>
          <button onclick="document.getElementById('success-toast').remove()" class="flex-shrink-0 text-white hover:text-gray-200">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
      </div>
    `;
    
    document.body.insertAdjacentHTML('beforeend', toastHtml);
    const toast = document.getElementById('success-toast');
    
    // Animate in
    setTimeout(() => {
      toast.classList.remove('translate-x-full');
    }, 100);
    
    // Auto remove after 3 seconds
    setTimeout(() => {
      if (toast) {
        toast.classList.add('translate-x-full');
        setTimeout(() => toast.remove(), 300);
      }
    }, 3000);
  };

  // Generate a unique barcode via backend, then update variant
  const generateUniqueBarcode = async (variant, index) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${BASEURL}/Product/GenerateBarcode`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed');
      const data = await response.json();
      const code = data.codeABarre || data.CodeABarre;
      if (!code) throw new Error('Empty');

      const newVariants = [...generatedVariants];
      newVariants[index].barCode = code;
      setGeneratedVariants(newVariants);

      showSuccessToast(`Code-barres généré: ${code}`);
      await handleBarcodeUpdate(variant, index, code);
    } catch (e) {
      console.error('Generate barcode error', e);
      showErrorToast('Erreur lors de la génération du code-barres');
    }
  };

  // FIXED: Enhanced barcode scanning with better mobile support and API integration
  const handleBarcodeScanning = async (index) => {
    setBarcodeScanning(prev => ({ ...prev, [index]: true }));
    
    try {
      // Check if we're in a secure context
      if (!window.isSecureContext) {
        throw new Error('Camera access requires HTTPS or localhost');
      }
      
      // Check if camera is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera not supported');
      }

      // Request camera access with mobile-optimized settings
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      
      const modalHtml = `
        <div id="barcode-scanner-modal" class="fixed inset-0 z-50 overflow-y-auto">
          <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div class="fixed inset-0 bg-gray-900 bg-opacity-90 transition-opacity"></div>
            <div class="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div class="bg-white px-6 pt-6 pb-4">
                <div class="flex items-start">
                  <div class="flex-shrink-0">
                    <div class="flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
                      <svg class="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"></path>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"></path>
                      </svg>
                    </div>
                  </div>
                  <div class="ml-4 w-full">
                    <h3 class="text-lg leading-6 font-semibold text-gray-900 mb-4">Scanner le code-barres</h3>
                    <div class="relative">
                      <video id="barcode-video" class="w-full h-64 bg-black rounded-lg border-2 border-gray-300" autoplay playsinline></video>
                      <canvas id="barcode-canvas" class="hidden"></canvas>
                      
                      <!-- Scanning overlay -->
                      <div class="absolute inset-0 pointer-events-none">
                        <div class="absolute inset-4 border-2 border-dashed border-white opacity-50 rounded-lg"></div>
                        <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                          <div class="w-48 h-32 border-2 border-red-500 rounded-lg relative">
                            <div class="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-red-500"></div>
                            <div class="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-red-500"></div>
                            <div class="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-red-500"></div>
                            <div class="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-red-500"></div>
                          </div>
                        </div>
                      </div>
                      
                      <div class="absolute bottom-2 left-2 right-2">
                        <div class="bg-black bg-opacity-70 text-white text-xs px-3 py-2 rounded text-center">
                          <div id="scan-status">Pointez la caméra vers le code-barres</div>
                          <div class="mt-1 text-yellow-300">Assurez-vous que le code-barres est bien éclairé et net</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row sm:justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                <button id="capture-btn" type="button" class="w-full sm:w-auto inline-flex justify-center rounded-lg border border-blue-300 shadow-sm px-4 py-2 bg-blue-50 text-sm font-medium text-blue-700 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                  📸 Capturer
                </button>
                <button id="manual-entry-btn" type="button" class="w-full sm:w-auto inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                  ✏️ Saisie manuelle
                </button>
                <button id="close-scanner" type="button" class="w-full sm:w-auto inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-red-600 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
                  ❌ Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      `;
      
      document.body.insertAdjacentHTML('beforeend', modalHtml);
      const modal = document.getElementById('barcode-scanner-modal');
      const videoElement = document.getElementById('barcode-video');
      const canvasElement = document.getElementById('barcode-canvas');
      const closeButton = document.getElementById('close-scanner');
      const manualEntryButton = document.getElementById('manual-entry-btn');
      const captureButton = document.getElementById('capture-btn');
      const scanStatus = document.getElementById('scan-status');
      
      videoElement.srcObject = stream;
      videoElement.play();

      let scanInterval = null;
      let isScanning = true;

      // Setup canvas for image processing
      const ctx = canvasElement.getContext('2d');

      // Try multiple scanning approaches
      const startScanning = () => {
        scanInterval = setInterval(async () => {
          if (!isScanning) return;

          try {
            // Method 1: Try BarcodeDetector API (if available)
            if ('BarcodeDetector' in window) {
              try {
                const barcodeDetector = new window.BarcodeDetector();
                const barcodes = await barcodeDetector.detect(videoElement);
                if (barcodes.length > 0) {
                  const barcode = barcodes[0].rawValue;
                  onBarcodeDetected(barcode);
                  return;
                }
              } catch (e) {
                console.log('BarcodeDetector failed, trying alternative methods');
              }
            }

            // Method 2: Canvas-based analysis (simplified pattern detection)
            if (videoElement.videoWidth > 0 && videoElement.videoHeight > 0) {
              canvasElement.width = videoElement.videoWidth;
              canvasElement.height = videoElement.videoHeight;
              ctx.drawImage(videoElement, 0, 0);
              
              // Simple barcode pattern detection (basic implementation)
              const imageData = ctx.getImageData(0, 0, canvasElement.width, canvasElement.height);
              const detected = detectBarcodePattern(imageData);
              if (detected) {
                scanStatus.textContent = '🔍 Code-barres détecté! Analysing...';
              }
            }
          } catch (error) {
            console.error('Scanning error:', error);
          }
        }, 500); // Scan every 500ms for better performance
      };

      // Simple barcode pattern detection
      const detectBarcodePattern = (imageData) => {
        // This is a very basic implementation
        // In a real app, you'd use a proper barcode library like QuaggaJS
        const data = imageData.data;
        let blackPixels = 0;
        let whitePixels = 0;
        
        // Sample pixels to detect bar patterns
        for (let i = 0; i < data.length; i += 16) { // Sample every 4th pixel
          const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
          if (avg < 128) blackPixels++;
          else whitePixels++;
        }
        
        // If we have a good mix of black and white, might be a barcode
        const ratio = blackPixels / (blackPixels + whitePixels);
        return ratio > 0.3 && ratio < 0.7;
      };

      // ENHANCED: onBarcodeDetected with API call
      const onBarcodeDetected = async (barcode) => {
        isScanning = false;
        clearInterval(scanInterval);
        
        const variant = generatedVariants[index];
        
        // Update the variant with the scanned barcode
        const newVariants = [...generatedVariants];
        newVariants[index].barCode = barcode; // Changed from .barcode to .barCode
        setGeneratedVariants(newVariants);
        
        // Close modal and stop scanning
        stream.getTracks().forEach(track => track.stop());
        modal.remove();
        setBarcodeScanning(prev => ({ ...prev, [index]: false }));
        
        // Show success toast
        showSuccessToast(`Code-barres scanné: ${barcode}`);

        // Make API call to update barcode
        await handleBarcodeUpdate(variant, index, barcode);
      };

      // Start scanning
      startScanning();

      // Manual capture button
      captureButton.addEventListener('click', () => {
        // Prompt user to manually enter what they see
        const userInput = prompt('Veuillez saisir le code-barres que vous voyez:');
        if (userInput && userInput.trim()) {
          onBarcodeDetected(userInput.trim());
        }
      });

      // Close button handler
      closeButton.addEventListener('click', () => {
        isScanning = false;
        clearInterval(scanInterval);
        stream.getTracks().forEach(track => track.stop());
        modal.remove();
        setBarcodeScanning(prev => ({ ...prev, [index]: false }));
      });

      // Manual entry button handler
      manualEntryButton.addEventListener('click', () => {
        isScanning = false;
        clearInterval(scanInterval);
        stream.getTracks().forEach(track => track.stop());
        modal.remove();
        setBarcodeScanning(prev => ({ ...prev, [index]: false }));
        openManualBarcodeModal(index, 'camera_fallback');
      });
      
    } catch (error) {
      console.error('Error accessing camera:', error);
      setBarcodeScanning(prev => ({ ...prev, [index]: false }));
      
      // Enhanced error handling with security context info
      let errorReason = 'camera_error';
      let errorMessage = 'Caméra non disponible.';
      
      if (!window.isSecureContext) {
        errorReason = 'insecure_context';
        errorMessage = 'Camera access requires HTTPS. Please access via https:// or use manual entry.';
      } else if (error.name === 'NotAllowedError') {
        errorReason = 'camera_permission';
        errorMessage = 'Permission caméra refusée.';
      }
      
      // Show error toast instead of modal for security issues
      if (!window.isSecureContext) {
        showErrorToast(errorMessage);
        // Auto-open manual entry after a delay
        setTimeout(() => {
          openManualBarcodeModal(index, errorReason);
        }, 2000);
      } else {
        openManualBarcodeModal(index, errorReason);
      }
    }
  };

  // Add error toast function
  const showErrorToast = (message) => {
    const toastHtml = `
      <div id="error-toast" class="fixed top-4 right-4 z-50 transform transition-all duration-300 translate-x-full">
        <div class="bg-gradient-to-r from-red-500 to-red-600 text-white px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3 max-w-sm">
          <div class="flex-shrink-0">
            <svg class="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <div class="flex-1">
            <p class="text-sm font-medium">${message}</p>
          </div>
          <button onclick="document.getElementById('error-toast').remove()" class="flex-shrink-0 text-white hover:text-gray-200">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
      </div>
    `;
    
    document.body.insertAdjacentHTML('beforeend', toastHtml);
    const toast = document.getElementById('error-toast');
    
    // Animate in
    setTimeout(() => {
      toast.classList.remove('translate-x-full');
    }, 100);
    
    // Auto remove after 5 seconds
    setTimeout(() => {
      if (toast) {
        toast.classList.add('translate-x-full');
        setTimeout(() => toast.remove(), 300);
      }
    }, 5000);
  };

  if (!enableVariants) return null;

  return (
    <div className="sm:col-span-6 mt-4">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-medium text-gray-700">Configuration des variantes</h4>
        <button
          type="button"
          className="inline-flex items-center px-3 py-1.5 border border-[#174540] rounded-md text-sm font-medium text-[#174540] bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
          onClick={() => setIsVariantModalOpen(true)}
        >
          <svg className="h-5 w-5 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Configurer les variantes
        </button>
      </div>

      {showGeneratedVariants && generatedVariants.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-md overflow-hidden mt-6">
          <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div>
              <h4 className="text-base font-medium text-gray-900">Variantes générées</h4>
              <p className="mt-1 text-sm text-gray-500">Configurez les détails pour chaque variante</p>
            </div>
            <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-[#174540] bg-opacity-10 text-[#174540]">
              {generatedVariants.length} variantes
            </span>
          </div>

          {/* Fixed height container with internal scrolling */}
          <div className="overflow-x-auto" style={{ maxHeight: '430px', overflowY: 'auto' }}>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-[#174540] focus:ring-[#174540] border-gray-300 rounded"
                      onChange={(e) => {
                        const newVariants = [...generatedVariants];
                        newVariants.forEach(v => v.selected = e.target.checked);
                        setGeneratedVariants(newVariants);
                      }}
                    />
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Variante
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Référence
                  </th>
                  {/* ADDED: Barcode column */}
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Code-barres
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Image
                  </th>

                  <th scope="col" className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {generatedVariants.map((variant, index) => (
                  <tr key={variant.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-[#174540] focus:ring-[#174540] border-gray-300 rounded"
                        checked={variant.selected || false}
                        onChange={(e) => {
                          const newVariants = [...generatedVariants];
                          newVariants[index].selected = e.target.checked;
                          setGeneratedVariants(newVariants);
                        }}
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {variant.combination}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4 2a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V4a2 2 0 00-2-2H4zm0 2h12v12H4V4zm2 3a1 1 0 00-1-1h5a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h5a1 1 0 100-2H7z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <input
                          type="text"
                          className={`block w-full pl-10 pr-3 py-2 focus:ring-[#174540] focus:border-[#174540] sm:text-sm border border-gray-300 rounded-md ${variant.serverData ? 'bg-gray-100 cursor-not-allowed' : 'bg-gray-50 shadow-sm'}`}
                          placeholder="SKU"
                          value={variant.sku || ''}
                          onChange={(e) => {
                            if (!variant.serverData) {
                              const newVariants = [...generatedVariants];
                              newVariants[index].sku = e.target.value;
                              setGeneratedVariants(newVariants);
                            }
                          }}
                          readOnly={variant.serverData}
                          disabled={variant.serverData}
                        />
                      </div>
                    </td>
                    {/* ENHANCED: Barcode field with API integration and loading states */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                          </svg>
                        </div>
                        <input
                          type="text"
                          className={`block w-full pl-10 pr-20 py-2 focus:ring-[#174540] focus:border-[#174540] sm:text-sm border border-gray-300 rounded-md shadow-sm ${
                            barcodeUpdateLoading[index] ? 'bg-yellow-50 border-yellow-300' : 'bg-gray-50'
                          }`}
                          placeholder="Code-barres"
                          value={variant.barCode || ''}
                          onChange={(e) => handleBarcodeInputChange(e, index)}
                          onFocus={(e) => handleBarcodeFocus(index, e.target.value)}
                          onBlur={(e) => handleBarcodeBlur(variant, index, e.target.value)}
                          disabled={barcodeUpdateLoading[index]}
                        />
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center space-x-1">
                          {/* Loading indicator */}
                          {barcodeUpdateLoading[index] && (
                            <div className="text-yellow-500" title="Mise à jour en cours...">
                              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => openManualBarcodeModal(index)}
                            disabled={barcodeUpdateLoading[index]}
                            className="text-gray-400 hover:text-[#174540] focus:outline-none focus:text-[#174540] transition-colors p-1 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Saisie manuelle"
                          >
                            <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleBarcodeScanning(index)}
                            disabled={barcodeScanning[index] || barcodeUpdateLoading[index]}
                            className="text-gray-400 hover:text-[#174540] focus:outline-none focus:text-[#174540] transition-colors disabled:opacity-50 disabled:cursor-not-allowed p-1"
                            title="Scanner avec la caméra"
                          >
                            {barcodeScanning[index] ? (
                              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => generateUniqueBarcode(variant, index)}
                            disabled={barcodeUpdateLoading[index]}
                            className="text-emerald-600 hover:text-emerald-800 focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed p-1"
                            title="Générer un code-barres unique"
                          >
                            <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </td>
                    {/* ... rest of the table columns remain the same ... */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col items-center">
                        <div className="flex flex-wrap gap-1 mb-1">
                          {(variant.imageUrl || []).map((imgUrl, imgIdx) => (
                            <div key={`var-img-${index}-${imgIdx}`} className="relative h-10 w-10">
                              <img
                                src={imgUrl}
                                alt={`${variant.combination} preview ${imgIdx + 1}`}
                                className="h-10 w-10 object-cover rounded-md"
                              />
                              <button
                                type="button"
                                onClick={() => handleDeleteVariantImage(index, imgUrl, imgIdx)}
                                className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-600 text-white flex items-center justify-center hover:bg-red-700 text-xs"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                          {(!variant.imageUrl || variant.imageUrl.length < 3) && (
                            <button
                              type="button"
                              onClick={() => triggerFileInput(index)}
                              className="flex items-center justify-center h-10 w-10 border border-dashed border-gray-300 rounded-md text-gray-400 hover:text-gray-500 hover:border-gray-400 cursor-pointer transition-colors"
                            >
                              {imageUploadLoading?.[index] ? (
                                <svg className="animate-spin h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              ) : (
                                <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                              )}
                              <input
                                ref={el => fileInputRefs.current[index] = el}
                                id={`variant-image-${index}`}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handleImageUpload(e, index)}
                              />
                            </button>
                          )}
                        </div>
                        {(variant.imageUrl?.length || 0) > 0 ? (
                          <span className="text-xs text-gray-500">{variant.imageUrl.length}/3</span>
                        ) : (
                          <span className="text-xs text-gray-500">Ajouter</span>
                        )}
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        type="button"
                        className="text-red-600 hover:text-red-900 transition-colors"
                        onClick={() => handleDeleteVariantClick(variant, index)}
                      >
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Manual Barcode Entry Modal remains the same */}
      {manualBarcodeModal.isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-900 bg-opacity-50 transition-opacity" onClick={closeManualBarcodeModal}></div>
            
            <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
              <div className="bg-white px-6 pt-6 pb-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
                      <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4 w-full">
                    <h3 className="text-lg leading-6 font-semibold text-gray-900 mb-2">
                      Saisir le code-barres
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">
                      {manualBarcodeModal.reason === 'camera_fallback' && 'Scanner fermé. Veuillez saisir le code-barres manuellement.'}
                      {manualBarcodeModal.reason === 'camera_error' && 'Caméra non disponible. Veuillez saisir le code-barres manuellement.'}
                      {manualBarcodeModal.reason === 'camera_permission' && 'Permission caméra refusée. Veuillez saisir le code-barres manuellement.'}
                      {manualBarcodeModal.reason === 'no_barcode_api' && 'Scanner non supporté sur cet appareil. Veuillez saisir le code-barres manuellement.'}
                      {manualBarcodeModal.reason === 'manual' && 'Saisissez le code-barres pour cette variante.'}
                    </p>
                    
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                        </svg>
                      </div>
                      <input
                        type="text"
                        className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                          manualBarcodeModal.error ? 'border-red-300 bg-red-50' : 'border-gray-300'
                        }`}
                        placeholder="Entrez le code-barres..."
                        value={manualBarcodeModal.inputValue}
                        onChange={(e) => setManualBarcodeModal(prev => ({
                          ...prev,
                          inputValue: e.target.value,
                          error: ''
                        }))}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleManualBarcodeSubmit();
                          }
                        }}
                        autoFocus
                      />
                    </div>
                    
                    {manualBarcodeModal.error && (
                      <div className="mt-2 flex items-center">
                        <svg className="h-4 w-4 text-red-500 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-sm text-red-600">{manualBarcodeModal.error}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row sm:justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                <button
                  type="button"
                  onClick={closeManualBarcodeModal}
                  className="w-full sm:w-auto inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleManualBarcodeSubmit}
                  className="w-full sm:w-auto inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Confirmer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VariantConfigurationSection;